<script>
  import ApplicationState from "../../../../../Stores/applicationStates"
  import axios from "axios"
  import Button from "../../Shared/Button.svelte"
  import ReArrangeLayers from "./reArrangeLayers.svelte"
  import BadgeState from "../../Stores/badgeState"
  import Palette from "../../Shared/Palette/index.svelte"
  import Shapes from "../Modals/badgeShapes/index.svelte"
  import Ribbons from "../Modals/badgeRibbons/index.svelte"
  import { push } from "svelte-spa-router"
  import ModalDelete from "../Modals/deleteModel.svelte"
  import notify from "../../../../../Script/notify"
  import { createEventDispatcher } from "svelte"
  import { deleteRequest, patchRequest } from "../../../../../Script/apiMethods"
  const dispatch = createEventDispatcher()

  export let baseCanvas
  export let loader
  export let loadingText

  let bgShapes = false
  let bgRibbon = false
  let operationId = ""
  let openCard = true
  let disableSaveBtn = false

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {closeNotification}
   */
  let closeNotification = {
    close() {},
  }

  let invalidNotification = {
    close() {},
  }

  const saveTemplate = async () => {
    try {
      closeNotification.close()
      $BadgeState.name = $BadgeState.name.trim()
      if (!$BadgeState.name) throw "Badge template name can't be empty"
      if (!$BadgeState.operations.length) throw "Please add atleast one layer"
      for (let i = 0; i < $BadgeState.operations.length; i++) {
        if ($BadgeState.operations[i].active) $BadgeState.operations[i].active = false
        if (!$BadgeState.operations[i].value.trim()) throw "Layer Title can't be empty"
      }

      disableSaveBtn = true
      loadingText = `${$BadgeState.name} badge template saving`

      let apiObj = {}

      await loadFontList()

      apiObj.name = $BadgeState.name
      apiObj.width = $BadgeState.baseInfo.width
      apiObj.height = $BadgeState.baseInfo.height
      apiObj.bgColour = $BadgeState.canvasColor
      apiObj.operations = JSON.stringify($BadgeState.operations)
      apiObj.fonts = JSON.stringify($BadgeState.fonts)
      let blobObject = await canvasToBlob(baseCanvas)

      // Create a new File object with the Blob and provide a default filename and type
      let file = new File([blobObject], `${$BadgeState.name + "-" + Date.now()}.png`, {
        type: "image/png",
      })

      apiObj.file = file

      // Save Badge Template
      await patchRequest(`/api/swagger/v2/templates/badges/${$BadgeState.baseInfo.id}`, apiObj, {
        headers: {
          "x-access-token": $ApplicationState.token,
          "Content-Type": "multipart/form-data",
        },
      })

      clearLocalStorage()
      push(`/templates/badge`)
      closeNotification = notify.success(`Badge Design Template <strong style="hyphens: auto;">${$BadgeState.name}</strong> saved successfully`) // Show a success notification
    } catch (error) {
      // console.error(error)
      const info = "PAGE: Badge Template Editor\nEVENT: While saving badge template"
      closeNotification = notify.danger(error, info)
    } finally {
      disableSaveBtn = false
    }
  }

  /**
   * Loads the list of fonts by sending a GET request to the server.
   * Updates the font list in the file template state.
   */
  const loadFontList = () => {
    return new Promise(async (resolve, reject) => {
      try {
        if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
        const { data } = await axios.get("/api/swagger/v2/fonts", {
          headers: {
            "x-access-token": $ApplicationState.token,
          },
        })
        // Error checking while API call
        if (data.error && !data.success) {
          throw data.errorCode
        }

        let fontList = [...data.fonts]

        $BadgeState.fonts = []

        for (let i = 0; i < $BadgeState.operations.length; i++) {
          if ($BadgeState.operations[i].type == "text") {
            for (let j = 0; j < fontList.length; j++) {
              if (fontList[j].name == $BadgeState.operations[i].fontFamily) {
                $BadgeState.fonts = [...$BadgeState.fonts, fontList[j].id]
              }
            }
          }
        }
        $BadgeState.fonts = [...new Set($BadgeState.fonts)]

        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Converts a canvas element to a Blob object.
   *
   * @param {HTMLCanvasElement} canvas - The canvas element to be converted.
   * @returns {Promise<Blob>} A Promise that resolves with the generated Blob object.
   */
  const canvasToBlob = (canvas) => {
    /**
     * @typedef {Blob} Blob - Represents a binary large object (Blob) that can store binary data.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Blob Blob}
     */

    return new Promise((resolve) => {
      /**
       * The `toBlob` method of the canvas element is used to convert it to a Blob.
       * @param {Blob} blob - The generated Blob object.
       */
      canvas.toBlob((blob) => {
        // Once the Blob is created, the Promise is resolved with the Blob object.
        resolve(blob)
      }, "image/png")
    })
  }

  let showDeleteModel = false
  // Deleting the file template for particular id

  const deleteTemplate = async (id) => {
    try {
      closeNotification.close()
      loader = true
      loadingText = `${$BadgeState.name} badge template deleting`

      await deleteRequest(`/api/swagger/v2/templates/badges/${id}`, {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      })

      closeNotification = notify.success(`<strong style="hyphens: auto;">${$BadgeState.name || "Untititled badge template"}</strong> deleted successfully`)
      clearLocalStorage()
      push("/templates/badge")
    } catch (error) {
      const info = "PAGE: Badge Template Editor\nEVENT: While deleting badge template"
      closeNotification = notify.danger(error, info)
    } finally {
      loader = false
    }
  }

  const clearLocalStorage = () => {
    $BadgeState = {
      name: "",
      baseInfo: {},
      operations: [],
      fonts: [],
      apiFonts: [],
      download: false,
      cursorPosition: {
        x: 0,
        y: 0,
      },
      canvasColor: "transparent",
      activeFontStyles: [],
      activeTextAlign: "center",
      activeTextColor: "#f2072e",
      goBack: false,
      resumeModal: false,
      footer: "GENERAL",
    }
  }

  /**
   * Regular expression pattern for allowed characters.
   * Matches any combination of uppercase letters, lowercase letters, digits, spaces, hyphens, parentheses, plus signs, ampersands, and periods.
   * @type {RegExp}
   */
  const allowedCharsRegex = /^[A-Z\d\s-()+&.]*$/i

  const checkSpecialChars = (e, name) => {
    const info = "PAGE: Badge Template Editor\nEVENT: Validating invalid characters for the badge template name during onBlur/ onPaste/ onKeypress"

    // Close any existing notifications
    invalidNotification.close()

    // Destructure the type and key properties from the event object
    let { type, key } = e

    // Check if it's a keypress event and the pressed key is not allowed

    if (type === "keypress" && !allowedCharsRegex.test(key)) {
      invalidNotification.close()

      // Ensure the name property is initialized as a string
      if (typeof $BadgeState.name !== "string") {
        $BadgeState.name = "" // Initialize it as an empty string
      }

      // Temporarily append the invalid key
      $BadgeState.name += key

      // Prevent the default keypress behavior
      e.preventDefault()

      // Show a danger notification
      invalidNotification = notify.danger("Only <strong>Letters</strong>,<strong>Numbers</strong> and <strong>-()+.&</strong> are allowed.", info)

      setTimeout(() => {
        // Remove the last invalid character
        $BadgeState.name = $BadgeState.name.slice(0, -1)
      }, 100)
    } else if (type === "paste" || type === "blur") {
      invalidNotification.close()

      // Prevent the default paste behavior
      // e.preventDefault()
      let pastedText

      if (type === "blur") {
        pastedText = $BadgeState.name
      } else {
        // Get the pasted text from the clipboard
        let clipboardData = e.clipboardData || window.clipboardData
        pastedText = clipboardData.getData("text/plain")
      }

      // Set the input value to the pasted text
      name = pastedText

      setTimeout(() => {
        // Remove any characters that are not letters, spaces, or specified special characters
        // let sanitizedText = pastedText.replace(new RegExp(`[^A-Z_\\-\\s\\d()+₹.]`, "gi"), "").trim()
        // Set the input value to the sanitized text
        // name = checkMaxCharacters(name)
        // Show a danger notification indicating the allowed characters and the removed non-specified characters

        if (!allowedCharsRegex.test(pastedText)) {
          let sanitizedText = pastedText.replace(/[^A-Za-z0-9\s-()+&.]/g, "").trim()
          if (sanitizedText !== pastedText) {
            invalidNotification = notify.danger("Only <strong>Letters</strong>, <Strong>Numbers</strong> and <br/> <strong>-()+.&</strong> are allowed. Removing non-specified Characters", info)
          }
          $BadgeState.name = sanitizedText
        }

        checkMaxCharacters()
        // $BadgeState.name = checkMaxCharacters($BadgeState.name)
        // $BadgeState.name = name
      }, 500)
    }
  }
  /**
   * @type {Number} - maximum character count for template name
   */
  let maxCharacters = 25

  const checkMaxCharacters = () => {
    //Deleting existing notification
    closeNotification.close()

    // ...
    if ($BadgeState.name && $BadgeState.name.length >= maxCharacters) {
      // Displaying notification for exceeding maximum characters
      closeNotification = notify.info(`Template name should not be more than ${maxCharacters} characters`)

      // Setting a timer to slice the name to maxCharacters
      setTimeout(() => {
        $BadgeState.name = $BadgeState.name.slice(0, maxCharacters)
      }, 20)
    }
  }
</script>

<div class="flex w-full flex-col gap-3 rounded-md bg-[#292a2f] p-5">
  <div class="flex w-full items-center justify-between gap-3">
    <Button
      on:click={() => {
        $BadgeState.name = $BadgeState.name.trim()
        showDeleteModel = true
      }}
      text="Delete"
      iconClass="fa-trash"
      addClass="bg-red-500 w-full"
    />
    <Button on:click={() => (openCard = !openCard)} text="Settings" iconClass="fa-gear" addClass="bg-extraBlue-500 w-full" />
    <Button state={disableSaveBtn} on:click={saveTemplate} text="Save" iconClass="fa-floppy-disk" addClass="bg-green-500 w-full" />
  </div>

  {#if openCard}
    <div class="group relative w-full rounded-md">
      <div class="absolute left-3 top-2.5 select-none text-sm font-bold text-gray-g00 {$BadgeState.name ? 'group-focus-within:text-extraBlue-500' : 'group-focus-within:text-red-500'}">NAME</div>
      <!-- on:blur={() => {
        $BadgeState.name = trimSpacesTemplateName($BadgeState.name)
      }} -->
      <input on:blur={checkSpecialChars} on:paste={checkSpecialChars} on:keypress={checkSpecialChars} on:keypress={checkMaxCharacters} type="text" bind:value={$BadgeState.name} required placeholder="Enter template name" class="w-full rounded-md border border-stone-700 bg-transparent py-2 pl-16 text-sm text-white outline-none invalid:animate-shake invalid:border-red-500 focus:ring-2 focus:ring-extraBlue-500 focus:invalid:ring-red-500" />
    </div>
    <div class="flex w-full gap-3 rounded-md">
      <Palette bind:headerBackground={$BadgeState.canvasColor} colorCount={17} disableGradient={false} BtnName="Choose Bg Color" position="bottom" />
      <Button
        state={!$BadgeState.operations.length}
        on:click={() => {
          dispatch("crop")
        }}
        text="Crop BG"
        addClass="bg-sky-500 w-full"
        iconClass="fa-crop-simple"
      />
    </div>
  {/if}
</div>

<ReArrangeLayers
  on:changeShape={(e) => {
    operationId = e.detail.id
    if (e.detail.type == "shape") bgShapes = true
    if (e.detail.type == "ribbon") bgRibbon = true

    // operationType = e.detail
  }}
/>
{#if bgShapes}
  <Shapes
    {operationId}
    bind:bgShapes
    on:click={() => {
      bgShapes = false
    }}
  />
{/if}
{#if bgRibbon}
  <Ribbons
    {operationId}
    bind:bgRibbon
    on:click={() => {
      bgRibbon = false
    }}
  />
{/if}

{#if showDeleteModel}
  <ModalDelete
    name={$BadgeState.name || "Untititled"}
    on:click={() => (showDeleteModel = false)}
    on:delete={() => {
      deleteTemplate($BadgeState.baseInfo.id)
      showDeleteModel = false
    }}
  />
{/if}
